import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { theme } from "../../theme/colors";
import { validateProfileLinkRows } from "../../utils/urlValidation";

const MAX_LINKS = 4;

const emptyRow = () => ({ title: "", url: "" });

export default function SocialLinksModal({ open, links = [], onClose, onSave }) {
  const [rows, setRows] = useState([emptyRow()]);
  const [urlErrors, setUrlErrors] = useState({});
  const [titleErrors, setTitleErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    const initial = Array.isArray(links) && links.length > 0
      ? links.slice(0, MAX_LINKS).map((l) => ({ title: l.title || "", url: l.url || "" }))
      : [emptyRow()];
    setRows(initial);
    setUrlErrors({});
    setTitleErrors({});
  }, [open, links]);

  const clearFieldError = (index, field) => {
    if (field === "url") {
      setUrlErrors((prev) => {
        if (!prev[index]) return prev;
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
    if (field === "title") {
      setTitleErrors((prev) => {
        if (!prev[index]) return prev;
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const handleRowChange = (index, field) => (e) => {
    const value = e.target.value;
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    clearFieldError(index, field);
  };

  const handleAddRow = () => {
    setRows((prev) => (prev.length >= MAX_LINKS ? prev : [...prev, emptyRow()]));
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [emptyRow()];
    });
    // Row indices shift — clear errors rather than reindex.
    setUrlErrors({});
    setTitleErrors({});
  };

  const handleSave = () => {
    const { urlErrors: nextUrlErrors, titleErrors: nextTitleErrors, cleaned } =
      validateProfileLinkRows(rows);

    if (Object.keys(nextUrlErrors).length > 0 || Object.keys(nextTitleErrors).length > 0) {
      setUrlErrors(nextUrlErrors);
      setTitleErrors(nextTitleErrors);
      return;
    }

    setUrlErrors({});
    setTitleErrors({});
    onSave(cleaned.slice(0, MAX_LINKS));
    onClose();
  };

  const hasErrors =
    Object.keys(urlErrors).length > 0 || Object.keys(titleErrors).length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="social-links-title"
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close"
        size="small"
        sx={{ position: "absolute", top: 8, right: 8, color: "#0f172a" }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <DialogTitle id="social-links-title" sx={{ fontWeight: 700, color: "#111", pb: 0.5 }}>
        Edit links
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ fontSize: 14, color: "#334155", mb: 2 }}>
          Add up to {MAX_LINKS} links to show on your profile. Each link needs a short title and a valid URL.
        </Typography>

        {hasErrors && (
          <Typography
            role="alert"
            sx={{ fontSize: 13, color: "#b91c1c", fontWeight: 600, mb: 2 }}
          >
            Fix the highlighted fields before saving.
          </Typography>
        )}

        <Stack spacing={2}>
          {rows.map((row, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <TextField
                label="Title"
                value={row.title}
                onChange={handleRowChange(index, "title")}
                variant="outlined"
                size="small"
                required
                error={Boolean(titleErrors[index])}
                helperText={titleErrors[index] || ""}
                FormHelperTextProps={{ sx: { mx: 0, mt: 0.75 } }}
                sx={{ flex: { xs: "1 1 100%", sm: "0 0 35%" } }}
                inputProps={{ maxLength: 60 }}
                placeholder="e.g. Website"
              />
              <TextField
                label="URL"
                value={row.url}
                onChange={handleRowChange(index, "url")}
                variant="outlined"
                size="small"
                required
                error={Boolean(urlErrors[index])}
                helperText={urlErrors[index] || ""}
                FormHelperTextProps={{ sx: { mx: 0, mt: 0.75 } }}
                sx={{ flex: 1, minWidth: 0 }}
                placeholder="https://example.com"
                inputProps={{ maxLength: 500 }}
              />
              <IconButton
                onClick={() => handleRemoveRow(index)}
                aria-label="Remove link"
                size="small"
                sx={{ color: "#64748b", mt: 0.25 }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>

        <Button
          onClick={handleAddRow}
          disabled={rows.length >= MAX_LINKS}
          startIcon={<AddIcon />}
          sx={{
            mt: 2,
            textTransform: "none",
            fontWeight: 600,
            color: theme.primary,
            "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
          }}
        >
          Add link
        </Button>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: "#0f172a",
            borderColor: "#0f172a",
            "&:hover": { borderColor: "#0f172a", bgcolor: "rgba(15,23,42,0.04)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 700,
            background: theme.gradient,
            "&:hover": { opacity: 0.95, background: theme.gradient },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
